import {useTranslation} from 'react-i18next';

import {Box, Chip, Typography} from '@mui/material';

import {WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT} from './workforceDashboardLayoutConstants';
import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {TableRowScorePill} from '../../../../../components/TableRowScorePill/TableRowScorePill';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {WorkforceDashboardRedeploymentCandidate} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardRedeploymentCandidate[] | null;
  onProfileClick: (resourceId: string) => void;
}

export const WorkforceDashboardRedeploymentCandidates = ({data, onProfileClick}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.redeploymentCandidates'});

  if (!data || data.length === 0) {
    return (
      <WorkforceDashboardSectionCard title={t('title')}>
        <EmptyStateCard />
      </WorkforceDashboardSectionCard>
    );
  }

  return (
    <WorkforceDashboardSectionCard
      title={t('title')}
      description={t('description') as string}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minWidth: 0,
          maxHeight: WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {data.map((item) => (
          <Box
            key={item.resourceId}
            onClick={() => onProfileClick(item.resourceId)}
            sx={{
              cursor: 'pointer',
              p: 1.25,
              borderRadius: '8px',
              border: `1px solid ${orangeColors[3]}`,
              '&:hover': {bgcolor: 'action.hover'},
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1,
                mb: 0.5,
                minWidth: 0,
              }}
            >
              <Typography variant='h6' sx={{minWidth: 0, flex: 1, overflowWrap: 'anywhere'}}>
                {item.publicId}
              </Typography>
              <Box sx={{flexShrink: 0}}>
                <TableRowScorePill score={item.overallScore} />
              </Box>
            </Box>
            <Typography
              variant='caption'
              sx={{display: 'block', color: 'text.secondary', mb: 1, overflowWrap: 'anywhere'}}
            >
              {item.seniority} · {item.companyDepartmentName}
            </Typography>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0}}>
              {item.strengths.map((strength) => (
                <Chip key={strength} size='small' label={strength} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </WorkforceDashboardSectionCard>
  );
};
